// 스타일 순서(캐스케이드)를 먼저 고정한다 — styles/index.ts
import "./styles";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter } from "react-router-dom";
import { App } from "./App";
import { createApiClient } from "./lib/http/client";
import type { FetchLike } from "./lib/http/client";
import { defaultBootDeps } from "./lib/boot/deps";
import type { BootDeps } from "./lib/boot/deps";
import { readConfig } from "./lib/config";
import { routerFuture, routes } from "./router";
import { consumeLaunchFragment } from "./store/session/fragment";
import { rememberLaunchNext } from "./store/session/launchNext";
import { captureBenchReturnFromUrl } from "./lib/bench/handoff";
import { SessionManager } from "./store/session/manager";
import { createSessionStore } from "./store/session/storage";

// 1) 앱을 그리기 전에 런치 프래그먼트(#ticket · #error · #bffToken)를 읽고 주소에서 지운다.
const launch = consumeLaunchFragment();
// 1-0) 4.6 §4.3 — 새 입장(티켓 · BFF 토큰)이면 `next`(예: course — Campus 미션)를 입장 화면에 맡긴다(없으면 옛 값을 지운다)
if (launch.ticket || launch.bffToken) rememberLaunchNext(launch.next);
// 1-1) Bench 에서 돌아온 폴백(?benchVersionHash=sha256:… — Field 탭이 없을 때) — 30분 맡기고 주소에서 지운다(lib/bench/handoff)
captureBenchReturnFromUrl();

async function boot() {
  const config = readConfig();
  let fetchImpl: FetchLike | undefined;
  let deps: BootDeps = defaultBootDeps(config.campusUrl);

  // 2) 개발 전용 가짜 API — 프로덕션 번들에는 들어가지 않는다(import.meta.env.DEV 로 제거).
  if (import.meta.env.DEV && config.fake) {
    const fake = await import("./dev/fake/fakeApi");
    fetchImpl = fake.createFakeFetch();
    deps = fake.fakeBootDeps(deps);
  }

  const api = createApiClient({ baseUrl: config.apiBase, fetch: fetchImpl });
  const manager = new SessionManager({ api, store: createSessionStore() });
  void manager.start(launch);

  const el = document.getElementById("root");
  if (!el) throw new Error("#root 가 없습니다");
  const router = createBrowserRouter(routes, { future: routerFuture });
  createRoot(el).render(
    <StrictMode>
      <App manager={manager} deps={deps} router={router} />
    </StrictMode>,
  );
}

void boot();
